import { Component, computed, inject, LOCALE_ID, signal } from '@angular/core';
import { InternalToolbar } from '../../components/internal-toolbar/internal-toolbar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { ApiService } from '../../api.service';
import { MatIcon } from '../../components/mat-icon/mat-icon';
import { TAGPermission } from '../../../models/tagpermission';
import { StatusCard } from '../../components/status-card/status-card';
import { TAGTodo, TodoStatus } from '../../../models/tagtodo';
import { TAGRequest } from '../../../models/tagrequest';
import { TAGInvTransaction } from '../../../models/taginventory';
import { TAGFinanceTransaction } from '../../../models/tagfinances';
import { ListCard } from '../../components/list-card/list-card';
import { formatDate } from '@angular/common';
import { TAGEvent } from '../../../models/tagevent';
import { TAGUser } from '../../../models/taguser';

@Component({
  selector: 'app-internal-dashboard',
  imports: [InternalToolbar, TranslatePipe, MatIcon, StatusCard, ListCard],
  providers: [DialogService, TranslateService],
  templateUrl: './internal-dashboard.html',
  styleUrl: './internal-dashboard.scss',
})
export class InternalDashboard {
  //private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  //private router = inject(Router);
  //private socket = inject(Socket);
  public api = inject(ApiService);
  private locale = inject(LOCALE_ID);
  //public printer = inject(PrinterService);

  users = signal<TAGUser[]>([]);

  externalLinks = [
    {name: "Nextcloud", link: "https://cloud.clrtd.com", icon: "cloud", disabled: !this.api.hasPermission(TAGPermission.login_cloud)},
    {name: "Wiki", link: "https://wiki.technikflg.com", icon: "quiz", disabled: !this.api.hasPermission(TAGPermission.login_wiki)},
    {name: "Mattermost", link: "https://mattermost.clrtd.com", icon: "forum", disabled: true}
  ];

  loadingTodos = signal<boolean>(true);
  openTodos = signal<TAGTodo[]>([]);
  todoItems = computed(() => {
    return this.openTodos().map((v, i, a) => {

      let addText = "";
      if (v.duedate) {
        if (v.duedate < new Date()) addText = this.translate.translate('internal.dashboard.todoOverdue')();
        else if (v.duedate.getTime() - new Date().getTime() < 1000 * 60 * 60 * 24 * 7)
          addText = formatDate(v.duedate, "E", this.locale);
        else
          addText = formatDate(v.duedate, "d MMM", this.locale);
      } else addText = this.translate.translate('todos.status.' + v.status.name)();

      return {
        text: v.description,
        add: addText,
        addClass: (v.duedate && v.duedate < new Date()? "text-red-600" : "")
      };
    });
  });

  loadingRequests = signal<boolean>(true);
  openRequests = signal<TAGRequest[]>([]);

  loadingEvents = signal<boolean>(true);
  openEvents = signal<TAGEvent[]>([]);
  eventItems = computed(() => {
    return this.openEvents().map((v, i, a) => {

      let addText = "";

      if (v.startdate.getTime() - new Date().getTime() < 1000 * 60 * 60 * 24 * 7)
        addText = formatDate(v.startdate, "E, HH:mm", this.locale);
      else
        addText = formatDate(v.startdate, "d MMM", this.locale);

      return {
        text: v.name,
        add: addText
      };
    });
  });

  loadingInventory = signal<boolean>(true);
  openInventory = signal<TAGInvTransaction[]>([]);
  missingItems = computed(() => {
    return this.openInventory().reduce((p, c, i) => p + c.items.filter((i) => !i.back).length, 0)
  });
  inventoryItems = computed(() => {
    return this.openInventory().map((v, i, a) => {

      let addText = this.translate.translate('internal.dashboard.invItemsBack',
        {
          back: v.items.filter((i) => !i.back).length,
          max: v.items.length
        })();

      let subText = "";
      let user = this.users().find(u => u._id == v.user);
      if (user) {
        subText = user.fullname + " • ";
      }
      subText += this.translate.translate('internal.dashboard.since', {date: formatDate(v.startdate, "d MMM", this.locale)})();

      return {
        text: v.location,
        add: addText,
        sub: subText
      };
    });
  });

  loadingFinances = signal<boolean>(true);
  openFinances = signal<TAGFinanceTransaction[]>([]);
  financeItems = computed(() => {
    return this.openFinances().map((v, i, a) => {

      let addText = (v.isIncome? "+" : "-") + " " + v.amount.toFixed(2) + "€";

      let subText = "";
      let user = this.users().find(u => u.username == v.user);
      if (user) {
        subText = user.fullname + " • ";
      }
      subText += formatDate(v.date, "d MMM", this.locale);

      return {
        text: v.reason,
        add: addText,
        sub: subText,
        addClass: (v.isIncome? "" : "text-red-600")
      };
    });
  });

  testitems = [
    {text: "Line 1", add: "3 items"},
    {text: "Line 2", add: "3 items", sub: "Subtext"},
    {text: "Line 3"},
    {text: "Line 4", sub: "Subtext"},
    {text: "Line 5", add: "3 items", sub: "Subtext"},
  ];

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      this.updateInformation();
    });
  }

  updateInformation() {
    this.loadUsers();
    this.updateTodos();
    this.updateRequests();
    this.updateEvents();
    this.updateInventory();
    this.updateFinances();
  }

  loadUsers() {
    if (!this.api.hasPermission(TAGPermission.view_users)) {
      return;
    }

    this.api.getUsers().then((users) => {
      this.users.set(users);
    }).catch((reason) => {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.translate('errors.couldNotLoadUsers')(),
        detail: reason.message,
      });
    });
  }

  updateTodos() {
    if (!this.api.hasPermission(TAGPermission.view_todos)) {
      this.loadingTodos.set(false);
      return;
    }

    this.api.getTodosByAssignee(this.api.user()._id).then((todos) => {
      this.openTodos.set(todos.filter(t => t.status == TodoStatus.OPEN || t.status == TodoStatus.INPROGRESS));
      this.loadingTodos.set(false);
    }).catch((reason) => {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.translate('errors.couldNotLoadTodos')(),
        detail: reason.message,
      });
    });
  }

  updateRequests() {
    if (!this.api.hasPermission(TAGPermission.view_requests)) {
      this.loadingRequests.set(false);
      return;
    }

    this.api.getRequests().then((requests) => {
      this.openRequests.set(requests.filter(r => r.enddate <= new Date()));
      this.loadingRequests.set(false);
    }).catch((reason) => {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.translate('errors.couldNotLoadRequests')(),
        detail: reason.message,
      });
    });
  }

  updateEvents() {
    if (!this.api.hasPermission(TAGPermission.view_events)) {
      this.loadingEvents.set(false);
      return;
    }

    this.api.getEvents().then((events) => {
      this.openEvents.set(events.filter(r => r.enddate >= new Date()));
      this.loadingEvents.set(false);
    }).catch((reason) => {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.translate('errors.couldNotLoadEvents')(),
        detail: reason.message,
      });
    });
  }

  updateInventory() {
    if (!this.api.hasPermission(TAGPermission.view_inventory)) {
      this.loadingInventory.set(false);
      return;
    }

    this.api.getInvTransactions().then((transactions) => {
      this.openInventory.set(transactions.filter(t => !t.allItemsBack()));
      this.loadingInventory.set(false);
    }).catch((reason) => {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.translate('errors.couldNotLoadInventoryTransactions')(),
        detail: reason.message,
      });
    });
  }

  updateFinances() {
    if (!this.api.hasPermission(TAGPermission.view_finances)) {
      this.loadingFinances.set(false);
      return;
    }

    this.api.getFinanceTransactions().then((transactions) => {
      this.openFinances.set(transactions.filter(t => t.pending));
      this.loadingFinances.set(false);
    }).catch((reason) => {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.translate('errors.couldNotLoadFinanceTransactions')(),
        detail: reason.message,
      });
    });
  }
}
